import { useEffect, useMemo, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ExportButtons } from "@/lib/exportData";
import Pagination, { usePagination } from "@/components/admin/Pagination";

export default function AdminCustomers() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/admin/customers").then((r) => setItems(r.data));
    }, []);

    const filtered = useMemo(
        () =>
            items.filter(
                (u) =>
                    !q ||
                    (u.name || "").toLowerCase().includes(q.toLowerCase()) ||
                    (u.email || "").toLowerCase().includes(q.toLowerCase()) ||
                    (u.phone || "").includes(q)
            ),
        [items, q]
    );

    const exportRows = useMemo(
        () =>
            filtered.map((u) => ({
                name: u.name,
                email: u.email,
                phone: u.phone,
                orders_count: u.orders_count,
                total_spend_inr: (u.total_spend || 0) / 100,
                joined: u.created_at,
            })),
        [filtered]
    );

    const { page, pageSize, pageItems, setPage, setPageSize } = usePagination(filtered, 20);

    return (
        <div data-testid="admin-customers-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="font-display text-4xl">Customers</h1>
                    <p className="text-muted-foreground mt-1">{items.length} registered customers</p>
                </div>
                <ExportButtons rows={exportRows} filename="xplosiv_clients" />
            </div>

            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-4">
                    <Input
                        placeholder="Search by name, email or phone…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="rounded-full bg-[#FAF7F1] border-[#E7DED2] max-w-md mb-4"
                        data-testid="admin-customers-search"
                    />
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs font-mono-ui text-muted-foreground border-b border-[#E7DED2]">
                                    <th className="py-2">Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Orders</th>
                                    <th>Spend</th>
                                    <th>Joined</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((u) => (
                                    <tr key={u.id} className="border-b border-[#F4EFE6]">
                                        <td className="py-3 font-medium">{u.name}</td>
                                        <td>{u.email}</td>
                                        <td>{u.phone || "—"}</td>
                                        <td>{u.orders_count}</td>
                                        <td>{formatPrice(u.total_spend || 0)}</td>
                                        <td className="text-xs text-muted-foreground">
                                            {new Date(u.created_at).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        total={filtered.length}
                        onPage={setPage}
                        onPageSize={setPageSize}
                    />
                </CardContent>
            </Card>
        </div>
    );
}
